import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import {
    Container, Typography, Box, Paper, Button, CircularProgress,
    Chip, Divider, Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingIcon from '@mui/icons-material/HourglassTop';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import moment from 'jalali-moment';
import { CONTRACT_LABELS, SHIFT_LABELS, formatToman } from './labels';
import { glass } from './theme';
import { Reveal } from './components/Reveal';

function ProviderDashboard() {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiClient.get('/api/dashboard/provider/')
            .then((response) => setDashboardData(response.data))
            .catch((error) => console.error(error))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    if (!dashboardData) {
        return <Typography sx={{ textAlign: 'center', mt: 4 }}>اطلاعات داشبورد قابل بارگذاری نیست.</Typography>;
    }

    const isVerified = !!dashboardData.is_verified_by_admin;

    const statCards = [
        { icon: CheckCircleIcon, color: 'primary.main', value: dashboardData.completed_jobs_count, label: 'دوره‌ی مراقبت تکمیل‌شده' },
        { icon: StarIcon, color: 'secondary.main', value: `${Number(dashboardData.average_rating || 0).toFixed(1)} از ۵`, label: 'میانگین امتیاز خانواده‌ها' },
        { icon: PaymentsIcon, color: 'primary.main', value: formatToman(dashboardData.total_earnings), label: 'مجموع دریافتی (تسویه‌شده)' },
        { icon: AccountBalanceWalletIcon, color: 'secondary.main', value: formatToman(dashboardData.pending_payout), label: 'در انتظار واریز (نزد پلتفرم)' },
    ];

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', pb: 6 }}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }} gutterBottom>داشبورد همیار</Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                خوش آمدید، {dashboardData.full_name || 'همیار گرامی'}
            </Typography>

            {/* وضعیت احراز هویت */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2.5, mb: 4, display: 'flex', alignItems: 'center', gap: 2,
                    borderColor: isVerified ? 'primary.main' : 'secondary.main',
                    bgcolor: isVerified ? 'primary.light' : 'secondary.light',
                }}
            >
                {isVerified ? <VerifiedIcon color="primary" /> : <PendingIcon color="secondary" />}
                <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        {isVerified ? 'پروفایل شما تأییدشده است' : 'پروفایل شما هنوز در انتظار تأیید است'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isVerified
                            ? 'پروفایل شما برای خانواده‌ها قابل مشاهده است و می‌توانید روی درخواست‌ها پیشنهاد ثبت کنید.'
                            : 'تا تکمیل احراز هویت (شاهکار)، استعلام سوءپیشینه و مصاحبه، پروفایل شما برای خانواده‌ها نمایش داده نمی‌شود.'}
                    </Typography>
                </Box>
                {!isVerified && (
                    <Button component={RouterLink} to="/profile" variant="contained" color="secondary" size="small">
                        تکمیل پروفایل
                    </Button>
                )}
            </Paper>

            {/* کارت‌های خلاصه عملکرد */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4,1fr)' }, gap: 2, mb: 5 }}>
                {statCards.map(({ icon: Icon, color, value, label }, i) => (
                    <Reveal key={label} delay={i * 0.07} y={14} sx={{ ...glass.card, p: 2.5, textAlign: 'center' }}>
                        <Icon sx={{ fontSize: 34, color }} />
                        <Typography variant="h6" sx={{ fontWeight: 800, mt: 1 }}>{value}</Typography>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                    </Reveal>
                ))}
            </Box>

            {/* لیست جدیدترین درخواست‌های موجود */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>جدیدترین درخواست‌های موجود</Typography>
                <Button component={RouterLink} to="/available-requests" size="small">مشاهده همه</Button>
            </Box>

            {dashboardData.newest_requests && dashboardData.newest_requests.length > 0 ? (
                <Box sx={glass.card}>
                    {dashboardData.newest_requests.map((req, index) => (
                        <React.Fragment key={req.id}>
                            <Box sx={{ p: 2.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 800 }}>
                                    #{req.id}
                                </Avatar>
                                <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                                    <Typography sx={{ fontWeight: 700 }}>
                                        {req.services.map((s) => s.name).join('، ') || 'بدون عنوان خدمت'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                                        <Chip size="small" label={CONTRACT_LABELS[req.contract_type] || req.contract_type} />
                                        <Chip size="small" label={SHIFT_LABELS[req.shift_type] || req.shift_type} />
                                        <Chip size="small" variant="outlined" label={moment(req.start_date).locale('fa').format('D MMMM')} />
                                        {req.city && <Chip size="small" variant="outlined" label={`${req.city}${req.district ? ` — ${req.district}` : ''}`} />}
                                    </Box>
                                </Box>
                                <Button component={RouterLink} to="/available-requests" variant="contained" size="small">
                                    مشاهده و ثبت پیشنهاد
                                </Button>
                            </Box>
                            {index < dashboardData.newest_requests.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </Box>
            ) : (
                <Box sx={{ ...glass.card, textAlign: 'center', p: 5 }}>
                    <SearchIcon sx={{ fontSize: 50, color: 'text.disabled' }} />
                    <Typography sx={{ mt: 2 }}>در حال حاضر درخواست جدیدی برای شما وجود ندارد.</Typography>
                    <Typography variant="body2" color="text.secondary">به محض ثبت درخواست جدید، در این بخش نمایش داده خواهد شد.</Typography>
                </Box>
            )}
        </Container>
        </Box>
    );
}

export default ProviderDashboard;
